import { useRef, useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { getMessageWithPlaceholders } from 'shared-locales';
import { ModalButtonWithForm } from 'components/common';
import {
  FormGroup,
  NumberStepper,
  Password,
  Usernames
} from 'components/common/form';
import { UsersListSelectedUsers } from 'components/users';

import type { FormApi } from 'informed';
import type { User } from 'shared-types';
import type { SelectedUser } from 'components/users/UsersListSelectedUsers';

interface Props {
  buttonIsDropdown?: boolean;
  targetUser?: Pick<User, 'id' | 'username'>;
  useAlternative?: boolean;
}

interface FormValues {
  expirationInDays: number;
  password: string;
  usernames?: string[];
}

const SetPassword = ({
  buttonIsDropdown = false,
  targetUser,
  useAlternative = false
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: {
      passwordMaximumLength,
      passwordDoNotForcePasswordChange,
      passwordManuallySetExpirationTimeInDays
    },
    user: loggedInUser
  } = useReduxState();

  const formApi = useRef<FormApi<FormValues>>(undefined);

  const [users, setUsers] = useState<SelectedUser[]>([]);
  const [visible, setVisible] = useState(false);

  const handleSubmit = async (values: FormValues) => {
    let selectedUsers: Partial<SelectedUser>[] = targetUser
      ? [{ identifier: targetUser.username }]
      : users.length
      ? users
      : values.usernames.map((username) => ({
          identifier: username
        }));

    try {
      const response = await api.post('/admin/users-set-password', {
        users: selectedUsers.map((user) => ({
          username: user.identifier,
          password: values.password
        })),
        expirationInDays: values.expirationInDays
      });

      const { errors, numberOfUpdatedPasswords } = response.data;
      Object.keys(errors).forEach((error) => {
        if (errors[error].length)
          dispatch(
            actions.addNotice({
              text: `${
                error === 'user_not_found'
                  ? messages.settingsAddAdminUserNotFound
                  : messages.authSetPasswordEmailUserError
              }:<br>${errors[error].join('<br>')}`,
              type: 'warning'
            })
          );
      });

      dispatch(
        actions.addNotice({
          text: targetUser
            ? messages.authSetPasswordSuccess
            : `${numberOfUpdatedPasswords} ${messages.authSetPasswordSuccessMultiple}`,
          type: 'success'
        })
      );
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateMaxPasswordLength = (value: string) => {
    if (value.length > passwordMaximumLength) {
      return getMessageWithPlaceholders({
        message: messages.authPasswordLengthErrorMax,
        placeholders: { length: passwordMaximumLength }
      });
    }
  };

  return (
    <ModalButtonWithForm
      apiRef={formApi}
      buttonCategory="success"
      buttonIcon="key"
      buttonIsDropdown={buttonIsDropdown}
      buttonText={messages.authSetPassword}
      initialValues={{
        expirationInDays: passwordManuallySetExpirationTimeInDays
      }}
      modalActionText={messages.commonSubmit}
      modalTitle={
        targetUser
          ? messages.authSetPasswordSetNewPassword
          : messages.authSetPasswordSetNewPasswordMultipleUsers
      }
      modalWidth="wide"
      onHide={() => setVisible(false)}
      onShow={() => setVisible(true)}
      onSubmit={handleSubmit}
    >
      {() => (
        <>
          {!targetUser && visible ? (
            useAlternative ? (
              <FormGroup
                htmlFor="usernames-set-password"
                label={messages.commonUsernames}
                labelTooltip={messages.emailAddressesTooltip}
                maxWidth="100%"
              >
                <Usernames
                  field="usernames"
                  id="usernames-set-password"
                  excludeUsersWithEmail={true}
                />
              </FormGroup>
            ) : (
              <UsersListSelectedUsers
                includeAdmins={loggedInUser?.roles.includes('superadmin')}
                onLoadedUsers={(users) => setUsers(users)}
                userSelectionParams={{ excludeUsersWithEmail: true }}
              />
            )
          ) : null}

          <FormGroup label={messages.commonPassword} maxWidth="100%">
            <div style={{ maxWidth: 300 }}>
              <Password
                field="password"
                formApi={formApi?.current}
                id={`password-${targetUser?.id}`}
                required={true}
                showGenerateOption={true}
                showPasswordInitial={true}
                validateAdditional={validateMaxPasswordLength}
              />
            </div>
          </FormGroup>

          {!passwordDoNotForcePasswordChange && (
            <NumberStepper
              field="expirationInDays"
              id={`expiration-in-days-${targetUser?.id || 0}`}
              label={messages.siteSettingsPasswordExpirationTimeInDays}
              max={100000}
              min={passwordManuallySetExpirationTimeInDays}
              stepValue={1}
              style={{ marginTop: 10 }}
            />
          )}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default SetPassword;
